import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTable, usePagination } from "react-table";
import HeaderMenu from "../Header/HeaderMenu";
import axiosApi from "../Utils/axiosApi";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Pagination,
  Chip,
  TextField,
  Grid,
  MenuItem,
  Snackbar,
  Alert
} from "@mui/material";
import "./UserListPage.css"; // ✅ reuse same CSS for mobile table

/* ---------------- Date Formatter ---------------- */
const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleString();
};

const AuthenticationAudit = () => {
  /* ------------------- State ------------------- */
  const [auditList, setAuditList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [companyList, setCompanyList] = useState([]);
  const [filters, setFilters] = useState({
    company: "",
    fromDate: "",
    toDate: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "warning" });

  // ------------------- Role -------------------
  
  const userRole = sessionStorage.getItem("userRole");
  const isAdmin = userRole === "ADMIN";

  /* ------------------- Fetch Companies ------------------- */
  useEffect(() => {
    axiosApi
      .post("/company/getCompany", {})
      .then((res) => {
        const list = Array.isArray(res.data)
          ? res.data
          : res.data.companies || [];
        setCompanyList(list);
      })
      .catch((err) => console.error("Company fetch error", err));
  }, []);

  /* ------------------- Fetch Audit List ------------------- */
  const fetchAudit = useCallback(async (payload) => {
    setLoading(true);
    try {
      const res = await axiosApi.post("/getAuthenticationAudit", payload);
      setAuditList(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Audit fetch error", err);
      setAuditList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /* ------------------- Search ------------------- */
  const handleSearch = () => {
    
    if (!isAdmin && !filters.company) {
      setSnackbar({ open: true, message: "Please select a company", severity: "warning" });
      return;
    }

    fetchAudit({
      companyId: filters.company || null,
      fromDate: filters.fromDate || null,
      toDate: filters.toDate || null,
    });
  };

  /* ------------------- Status Chip ------------------- */
  const renderStatusChip = (status) =>
    status === "LOGIN-SUCESS" ? (
      <Chip label="Success" color="success" size="small" />
    ) : (
      <Chip label="Failure" color="error" size="small" />
    );

  /* ------------------- Columns ------------------- */
  const columns = useMemo(
    () => [
      { Header: "User Name", accessor: "userName" },
      { Header: "Company Name", accessor: "companyName" },
      {
        Header: "Login Time",
        accessor: "loginDateTime",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "Logout Time",
        accessor: "logoutDateTime",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: "Status",
        accessor: "authStatus",
        Cell: ({ value }) => renderStatusChip(value),
      },
      { Header: "IP Address", accessor: "clientIp" },
      { Header: "Device Info", accessor: "deviceInfo" },
      { Header: "Location", accessor: "geoLocation" },
      { Header: "Failure Reason", accessor: "failureReason" },
    ],
    []
  );

  /* ------------------- React Table ------------------- */
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    state: { pageIndex, pageSize },
    setPageSize,
    pageCount,
  } = useTable(
    {
      columns,
      data: auditList,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    usePagination
  );

  return (
    <>
      <HeaderMenu />

      <div className="user-list">
        <Typography variant="h5" sx={{ mb: 2 }}>
          Authentication Audit
        </Typography>

        {/* ------------------- Filters ------------------- */}
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                size="small"
                fullWidth
                label={isAdmin ? "Company" : "Company *"}
                value={filters.company}
                required={!isAdmin}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, company: e.target.value }))
                }
              >
                <MenuItem value="">All Companies</MenuItem>
                {companyList.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.companyName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={filters.fromDate}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, fromDate: e.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                type="date"
                size="small"
                fullWidth
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={filters.toDate}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, toDate: e.target.value }))
                }
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                disabled={!isAdmin && !filters.company} // Prevent non-admin search without company
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* ------------------- Snackbar ------------------- */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* ------------------- Table ------------------- */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
            <Typography sx={{ mt: 1 }}>
              Loading authentication audit...
            </Typography>
          </Box>
        ) : (
          <div className="table-container">
            <table {...getTableProps()} className="table">
              <thead>
                {headerGroups.map((hg) => (
                  <tr {...hg.getHeaderGroupProps()} key={hg.id}>
                    {hg.headers.map((col) => (
                      <th {...col.getHeaderProps()} key={col.id}>
                        {col.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} align="center">
                      No data found
                    </td>
                  </tr>
                ) : (
                  page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} key={row.id}>
                        {row.cells.map((cell) => (
                          <td
                            key={cell.column.id}
                            data-label={cell.column.Header} // ✅ mobile-friendly
                          >
                            {cell.render("Cell") || "-"}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ------------------- Pagination ------------------- */}
        {!loading && auditList.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <Pagination
              count={pageCount}
              page={pageIndex + 1}
              onChange={(e, value) => gotoPage(value - 1)}
              color="primary"
            />
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[5, 10, 20, 50].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Box>
        )}
      </div>
    </>
  );
};

export default AuthenticationAudit;
